type ApiAuthHandlers = {
  getCsrfToken: () => string | null;
  onUnauthorized: () => void | Promise<void>;
  onForbidden: () => void | Promise<void>;
};

let authHandlers: ApiAuthHandlers | null = null;

export function registerApiAuthHandlers(handlers: ApiAuthHandlers): () => void {
  authHandlers = handlers;
  return () => {
    if (authHandlers === handlers) authHandlers = null;
  };
}

export function getApiBaseUrl(): string {
  if (typeof window === "undefined") {
    const internalApiUrl = process.env.INTERNAL_API_URL || "http://asadal:8000";
    return `${internalApiUrl.replace(/\/$/, "")}/api`;
  }
  return "/api";
}

/** 웹소켓 주소(`/api/...` 경로를 받아 ws(s)://현재 호스트 붙임). 쿠키 인증은 브라우저가 붙인다. */
export function getApiWsUrl(path: string): string {
  if (typeof window === "undefined") {
    return `${getApiBaseUrl().replace(/^http/, "ws")}${path}`;
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api${path}`;
}

function buildApiUrl(path: string): URL {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${path}`;
  return typeof window === "undefined"
    ? new URL(url)
    : new URL(url, window.location.origin);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function isUnsafeMethod(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const request = input instanceof Request ? input : null;
  const method = (init.method || request?.method || "GET").toUpperCase();
  const headers = new Headers(request?.headers);
  new Headers(init.headers).forEach((value, key) => headers.set(key, value));

  if (isUnsafeMethod(method) && typeof window !== "undefined") {
    const csrfToken = authHandlers?.getCsrfToken();
    if (!csrfToken) {
      throw new ApiError(403, "보안 토큰이 없습니다. 페이지를 새로고침해 주세요.");
    }
    headers.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(input, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
    cache: init.cache ?? "no-store",
  });

  if (response.status === 401) {
    await authHandlers?.onUnauthorized();
  } else if (response.status === 403) {
    await authHandlers?.onForbidden();
  }
  return response;
}

async function errorFromResponse(response: Response): Promise<ApiError> {
  const fallback = `요청에 실패했습니다. (${response.status})`;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const body = (await response.json().catch(() => null)) as
      | { detail?: unknown; message?: unknown }
      | null;
    const detail = body?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : typeof body?.message === "string"
          ? body.message
          : fallback;
    return new ApiError(response.status, message, detail);
  }
  const text = await response.text().catch(() => "");
  return new ApiError(response.status, text.trim() || fallback);
}

export async function parseApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) throw await errorFromResponse(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;
  const contentType = response.headers.get("content-type") || "";
  return (contentType.includes("application/json") ? JSON.parse(text) : text) as T;
}

async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  params?: Record<string, unknown>,
): Promise<T> {
  const url = buildApiUrl(path);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return parseApiResponse<T>(await apiFetch(url, init));
}

export function apiGet<T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<T> {
  return apiRequest<T>(path, { method: "GET" }, params);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "DELETE",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export interface ScreenshotUploadResponse {
  status: string;
  path: string;
  filename: string;
}

export function apiUploadScreenshot(file: File): Promise<ScreenshotUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<ScreenshotUploadResponse>("/admin/screenshot", {
    method: "POST",
    body: formData,
  });
}
